/**
 * use-board-filters.ts
 *
 * Sincronizza i filtri della Kanban Planner (/board) con i search params dell'URL,
 * sul modello di `use-request-type-page-state.ts`. `useSearch({ strict: false })` evita
 * l'invariant di TanStack Router quando il match esatto della rotta può fallire (stesso
 * approccio usato per le pagine tipologia), quindi non serve un `from` legato all'id rotta.
 */

import { useCallback } from 'react';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { useOktaAuth } from '../providers/okta-auth-provider';
import type { KanbanBoardFilters } from '../types/api';

const BOARD_PATH = '/board';

/**
 * Rimuove valori undefined/vuoti dai filtri, così l'URL resta pulito (niente `?dateField=&from=`).
 */
function cleanFilters(filters: Partial<KanbanBoardFilters>): KanbanBoardFilters {
  const cleaned: KanbanBoardFilters = {};
  if (filters.requestId !== undefined) cleaned.requestId = filters.requestId;
  if (filters.assigneeIds && filters.assigneeIds.length > 0) cleaned.assigneeIds = filters.assigneeIds;
  if (filters.dateField) cleaned.dateField = filters.dateField;
  if (filters.from) cleaned.from = filters.from;
  if (filters.to) cleaned.to = filters.to;
  return cleaned;
}

export function useBoardFilters() {
  const navigate = useNavigate();
  const location = useLocation();
  // Pathname runtime come fallback, per coerenza con use-request-type-page-state.ts:
  // garantisce che il navigate aggiorni i search params della rotta in cui il componente
  // è realmente montato anche in scenari di match non esatto.
  const currentPath = location.pathname || BOARD_PATH;
  const { userInfo } = useOktaAuth();

  const filters = useSearch({ strict: false }) as KanbanBoardFilters;

  const updateFilters = useCallback(
    (partial: Partial<KanbanBoardFilters>) => {
      navigate({
        to: currentPath,
        search: (prev: Record<string, unknown>) => cleanFilters({ ...(prev ?? {}), ...partial }),
        replace: true,
      } as any);
    },
    [navigate, currentPath]
  );

  const resetFilters = useCallback(() => {
    navigate({ to: currentPath, search: {} as any, replace: true } as any);
  }, [navigate, currentPath]);

  const myUserId = userInfo?.id;
  const isOnlyMine =
    myUserId != null &&
    !!filters.assigneeIds &&
    filters.assigneeIds.length === 1 &&
    filters.assigneeIds[0] === myUserId;

  const toggleOnlyMine = useCallback(() => {
    if (myUserId == null) return;
    updateFilters({ assigneeIds: isOnlyMine ? undefined : [myUserId] });
  }, [myUserId, isOnlyMine, updateFilters]);

  return {
    filters,
    updateFilters,
    resetFilters,
    myUserId,
    isOnlyMine,
    toggleOnlyMine,
  };
}
